import type { ConditionOperator } from '@reach/shared-types/conditions';
import React from 'react';
import { acceptsMultipleValues, getOperatorsForFieldType } from './field-type-operators';
import type { BuilderFieldType } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  greater_than: 'greater than',
  less_than: 'less than',
  exists: 'exists',
  not_exists: 'does not exist',
  equals_month_day: 'Recurring Date',
  equals_month_day_year: 'Exact Date',
  between_month_day: 'Recurring Date Range',
  between_month_day_year: 'Exact Date Range',
  array_contains: 'array contains',
  contains: 'contains',
  not_contains: 'does not contain',
  in: 'is one of',
};

/**
 * Get the human-readable label for an operator, with special handling for date fields.
 * For date fields, "greater than" becomes "after" and "less than" becomes "before".
 */
const getOperatorLabel = (
  operator: ConditionOperator,
  fieldType: BuilderFieldType,
  valueCount: number,
): string => {
  // Special case for date fields
  if (fieldType === 'date') {
    if (operator === 'greater_than') return 'after';
    if (operator === 'less_than') return 'before';
  }
  const usesIsNoneOfLabel =
    operator === 'not_equals' && valueCount > 1 && acceptsMultipleValues(operator, fieldType);
  if (usesIsNoneOfLabel) return 'is none of';
  return OPERATOR_LABELS[operator] ?? operator;
};

interface OperatorSelectorProps {
  fieldType: BuilderFieldType;
  value: ConditionOperator;
  onChange: (value: ConditionOperator) => void;
  disabled?: boolean;
  valueCount: number;
}

/**
 * Type-aware operator dropdown. Only shows operators valid for the selected field type.
 */
export const OperatorSelector: React.FC<OperatorSelectorProps> = ({
  fieldType,
  value,
  onChange,
  disabled = false,
  valueCount,
}) => {
  const options = getOperatorsForFieldType(fieldType);
  const isValid = options.includes(value);
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value as ConditionOperator);
  };

  return (
    <select
      className={styles.selectInput}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      aria-label="Condition operator">
      {!isValid && <option value={value}>{getOperatorLabel(value, fieldType, valueCount)}</option>}
      {options.map(op => (
        <option key={op} value={op}>
          {getOperatorLabel(op, fieldType, valueCount)}
        </option>
      ))}
    </select>
  );
};
